import { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { companyEmail } from '../../helpers';
import { colors, mobile, notMobile } from '../../styles';

// Billing B6.1a: one policy for every venue, from yedding-brain D-0014 and
// D-0030. It replaces the host's 48-hour / 14-day / 21-day "full refund" line.
// "Amount", never "half" (D-0031). B5.1 adds the 2-week charge to the split line.
// Closed, the box ends after "We review each" and an arrow opens the rest
// (Braeden, 2026-10-21): 3 lines on phones, 2 on wider screens.

const LINE = 22; // px
const PHONE_LINES = 3;
const DESKTOP_LINES = 2;

const Box = styled.div`
  padding: 16px;
  background-color: ${colors.secondaryBackground};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  font-size: 14px;
  font-weight: 400;
  line-height: ${LINE}px;
`;

const Heading = styled.div`
  font-weight: 700;
  margin-bottom: 10px;
`;

const closed = (lines) => css`
  max-height: ${LINE * lines}px;
  overflow: hidden;
  -webkit-mask-image: linear-gradient(to bottom, #000 70%, rgba(0, 0, 0, 0.35));
  mask-image: linear-gradient(to bottom, #000 70%, rgba(0, 0, 0, 0.35));
`;

const Body = styled.div`
  ${({ $open }) =>
    !$open &&
    css`
      ${closed(DESKTOP_LINES)}
      @media (max-width: ${mobile}) {
        ${closed(PHONE_LINES)}
      }
    `}
`;

// Desktop: the two sentences run on as one paragraph. Phones: two lines.
const Sentence = styled.div`
  @media (min-width: ${notMobile}) {
    display: inline;
  }
`;

const Split = styled.div`
  padding-top: 5px;
`;

const Toggle = styled.button`
  align-self: center;
  margin: 4px 0 -8px;
  padding: 4px 16px;
  border: none;
  background: none;
  color: inherit;
  line-height: 0;
  cursor: pointer;

  svg {
    transition: transform 0.2s;
    ${({ $open }) => $open && 'transform: rotate(180deg);'}
  }
`;

const RefundPolicy = ({ splitPayment }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Heading>Cancellation Policy</Heading>
      <Body id="refund-policy-body" $open={open}>
        <Sentence>
          The initial 50% payment is <strong>non-refundable</strong>.
        </Sentence>{' '}
        <Sentence>
          For a refund of the remaining amount, email <a href={`mailto:${companyEmail}`}>{companyEmail}</a>. We review each
          request. We generally do not give this refund if you cancel within 30 days of your event. An emergency can be an
          exception.
        </Sentence>
        {splitPayment && <Split>Split payment: The remaining 50% is due 3 days before the event.</Split>}
      </Body>
      <Toggle
        type="button"
        $open={open}
        aria-expanded={open}
        aria-controls="refund-policy-body"
        aria-label={open ? 'Show less of the cancellation policy' : 'Show all of the cancellation policy'}
        onClick={() => setOpen(!open)}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M3 6l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Toggle>
    </Box>
  );
};

RefundPolicy.propTypes = {
  splitPayment: PropTypes.bool
};

export default RefundPolicy;
